import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as api from '../api/api';

interface CategoryState {
  categories: string[];
  subcategories: { [key: string]: string[] };
  predefined_categories: string[];
  predefined_subcategories: { [key: string]: string[] };
  loading: boolean;
  error: string | null;
}

const initialState: CategoryState = {
  categories: [],
  subcategories: {},
  predefined_categories: [],
  predefined_subcategories: {},
  loading: false,
  error: null,
};

export const fetchCategories = createAsyncThunk(
  'categories/fetchCategories',
  async () => {
    const response = await api.fetchCategories();
    return response;
  }
);

export const addCategory = createAsyncThunk(
  'categories/addCategory',
  async (category: string) => {
    const response = await api.addCategory(category);
    return response;
  }
);

export const addSubcategory = createAsyncThunk(
  'categories/addSubcategory',
  async ({ category, subcategory }: { category: string; subcategory: string }) => {
    const response = await api.addSubcategory(category, subcategory);
    return response;
  }
);

export const deleteCategory = createAsyncThunk(
  'categories/deleteCategory',
  async (category: string) => {
    const response = await api.deleteCategory(category);
    return response;
  }
);

export const deleteSubcategory = createAsyncThunk(
  'categories/deleteSubcategory',
  async ({ category, subcategory }: { category: string; subcategory: string }) => {
    const response = await api.deleteSubcategory(category, subcategory);
    return response;
  }
);

export const renameCategory = createAsyncThunk(
  'categories/renameCategory',
  async ({ oldName, newName }: { oldName: string; newName: string }) => {
    const response = await api.renameCategory(oldName, newName);
    return response;
  }
);

export const renameSubcategory = createAsyncThunk(
  'categories/renameSubcategory',
  async ({ category, oldName, newName }: { category: string; oldName: string; newName: string }) => {
    const response = await api.renameSubcategory(category, oldName, newName);
    return response;
  }
);

const categorySlice = createSlice({
  name: 'categories',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch categories
      .addCase(fetchCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = action.payload.categories;
        state.subcategories = action.payload.subcategories;
        state.predefined_categories = action.payload.predefined_categories;
        state.predefined_subcategories = action.payload.predefined_subcategories;
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch categories';
      })
      // Add/Delete/Rename operations all return the new state
      .addCase(addCategory.fulfilled, (state, action) => {
        state.categories = action.payload.categories;
        state.subcategories = action.payload.subcategories;
      })
      .addCase(addSubcategory.fulfilled, (state, action) => {
        state.categories = action.payload.categories;
        state.subcategories = action.payload.subcategories;
      })
      .addCase(deleteCategory.fulfilled, (state, action) => {
        state.categories = action.payload.categories;
        state.subcategories = action.payload.subcategories;
      })
      .addCase(deleteSubcategory.fulfilled, (state, action) => {
        state.categories = action.payload.categories;
        state.subcategories = action.payload.subcategories;
      })
      .addCase(renameCategory.fulfilled, (state, action) => {
        state.categories = action.payload.categories;
        state.subcategories = action.payload.subcategories;
      })
      .addCase(renameSubcategory.fulfilled, (state, action) => {
        state.categories = action.payload.categories;
        state.subcategories = action.payload.subcategories;
      });
  },
});

export default categorySlice.reducer;
